import { HttpHeaders, HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { environment as testCasesEnv } from 'src/environments/environment.local';
import { points } from '@shared/constants/points';
import { PATCH_OP, RestMethod } from '@shared/enums/rest-method';
import { IPatchStructure, IRequestParams } from '@shared/interfaces/rest/Irequest-params';
import { tap } from 'rxjs/operators';
import { EventsService } from '@shared/services/common/services/events.service';
import { StatusCode } from '@shared/enums/status-code';
import { Guid } from 'guid-typescript';
import { IResponseMessage } from '@shared/interfaces/certify/IResponse-message';

export abstract class AbsRestInfraestructure {
  private point: string;
  private envConfiguration: any;
  private defNotifier: boolean;

  constructor(
    protected http: HttpClient,
    private event: EventsService,
  ) {}

  /**
   * Valide minum requirement params to make request
   * @param params request params
   * @returns Server response.
   */
  protected validateRequest<T>(params: IRequestParams): Observable<T> {
    if (params.point === null || params.point === undefined || params.point === '') {
      return throwError(() => new Error('Invalid params'));
    } else {
      this.defNotifier = params.defaultNotifier ?? false;
      return this.doRequest(params);
    }
  }

  /**
   * Generic request handler
   * @param params params requestor
   * @returns Server response
   */
  private doRequest<T>(params: IRequestParams): Observable<T> {
    const loadOnRequest = params.showLoad ?? true;

    this.point = this.getPointsSquema(params);
    let url = '';

    if (params.fullparameter !== null && params.fullparameter !== '' && params.fullparameter !== undefined) {
      if (params.fullparameter.startsWith('/')) {
        url = this.point + params.fullparameter;
      } else {
        url = this.point + '?' + params.fullparameter;
      }
    } else {
      url = this.point;
    }

    if (navigator.onLine) {
      this.event.emit('loadEvt', loadOnRequest);
    } else {
      this.event.emit('notifierMessage', { status: StatusCode.SERVICE_UNAVAILABLE });
    }

    const headers = this.createAuthorizationHeader(params);
    url = this.valideAutoPatchBody(params, url);

    if (params.verb === RestMethod.GET) {
      return this.http.get<T>(url, headers).pipe(
        tap({
          next: (data) => {
            this.log(url, data);
          },
          error: (err) => {
            this.error(err);
          },
        }),
      );
    }
    if (params.verb === RestMethod.POST) {
      return this.http.post<T>(url, params.body, headers).pipe(
        tap({
          next: (data) => {
            this.log(url, data);
          },
          error: (err) => {
            this.error(err);
          },
        }),
      );
    }
    if (params.verb === RestMethod.PATCH) {
      if (params?.body.length > 0) {
        return this.http.patch<T>(url, params.body, headers).pipe(
          tap({
            next: (data) => {
              this.log(url, data);
            },
            error: (err) => {
              this.error(err);
            },
          }),
        );
      } else {
        this.log('', null);
        return of({ message: 'views.globals.no-changes' } as unknown as T);
      }
    }
    if (params.verb === RestMethod.PUT) {
      return this.http.put<T>(url, params.body, headers).pipe(
        tap({
          next: (data) => {
            this.log(url, data);
          },
          error: (err) => {
            this.error(err);
          },
        }),
      );
    }
    if (params.verb === RestMethod.DELETE) {
      const options = {
        headers: headers.headers,
        body: params.body,
      };

      return this.http.delete<any>(url, options).pipe(
        tap({
          next: (data) => {
            this.log(url, data);
          },
          error: (err) => {
            this.error(err);
          },
        }),
      );
    }
  }

  /**
   * Url Builder consumpion with version or without version.
   * @param params request parameters
   * @returns Url Created by current Environment
   */
  private getPointsSquema(params: IRequestParams): string {
    let basePoint = '';
    const listGroupPoints = this.getListPoints();
    let pointFound = false;
    const versionRegex = /^v\d+\//;
    const uriParam = this.validateForUTestResponse(params.point);

    for (const baseDomain of Object.keys(listGroupPoints)) {
      const groupPoints = listGroupPoints[baseDomain];
      for (const x of groupPoints) {
        Object.getOwnPropertyNames(x).forEach((bc) => {
          x[bc].forEach((p: any) => {
            for (const d of p.points) {
              for (const y of Object.keys(d)) {
                if (y === uriParam.point) {
                  const pointUrl = this.sanitizePoint(d[y], uriParam.localResponse);
                  basePoint = versionRegex.test(pointUrl)
                    ? `${this.envConfiguration.APIM}${p.controller}`
                    : `${this.envConfiguration.APIM}${bc}/${p.controller}`;

                  basePoint += pointUrl !== '' ? `/${pointUrl}` : '';

                  if (p?.port && this.envConfiguration.domain.includes('localhost')) {
                    let domain = this.envConfiguration.domain.replace('4200', p.port);
                    domain = domain.replace('http:', 'https:');
                    basePoint = basePoint.replace(`${this.envConfiguration.APIM}${bc}`, `${domain}`);
                  }
                  pointFound = true;

                  localStorage.setItem('pointSchema', baseDomain);
                  break;
                }
              }
              if (pointFound) {
                break;
              }
            }
          });
        });

        if (pointFound) {
          break;
        }
      }
      if (pointFound) {
        break;
      }
    }
    return basePoint;
  }

  /**
   * Prevents mock request on non local/dev environment
   * @param point url created
   * @returns sanitized url by environment.
   */
  private sanitizePoint(point: string, utCases: string): string {
    let pointSanitized: string;

    if (this.envConfiguration.domain.includes('localhost')) {
      pointSanitized = utCases !== '' ? (point !== '' ? `${utCases}/${point}` : utCases) : point;
    } else {
      if (point.includes('mock') && this.envConfiguration.APIM.indexOf('development') === -1) {
        pointSanitized = point.replace(/\/?mock\/?/g, '');
        console.warn(
          `PLEASE REMOVE 'MOCK WORD' on ${point} AT SOON, IT WAS REMOVED AUTOMATICALLY, => ${this.envConfiguration.domain}`,
        );
      } else {
        pointSanitized = point;
      }
    }

    pointSanitized = pointSanitized.replace('//', '/');
    this.sendIdentifierMockUsing(pointSanitized);
    return pointSanitized;
  }

  sendIdentifierMockUsing(pointSanitized: string): void {
    setTimeout(() => {
      this.event.emit('loadByMock', pointSanitized.includes('mock'));
    }, 200);
  }

  /**
   * Retrieve a list of enpoints configured on schema_api
   * @returns Enpoint List
   */
  private getListPoints(): any {
    const pointSchema: any = points;
    return Object.assign(Object.create(null), pointSchema.schema_api);
  }

  /**
   * If original && modified is configured on body then validate
   * changes between bodies and change rest method to send a patch
   * request.
   * @param params
   * @param url original URL path
   * @returns url with identifier to request
   */
  valideAutoPatchBody(params: IRequestParams, url: string): string {
    if (params.verb === RestMethod.PUT && params.body?.original && params.body?.modified) {
      const original = params.body.original;
      const modified = params.body.modified;

      const patchBody: IPatchStructure[] = [];

      for (const key in modified) {
        if (Object.prototype.hasOwnProperty.call(modified, key)) {
          if (modified[key] !== original[key]) {
            const patchValue: IPatchStructure = {
              op: PATCH_OP.REPLACE,
              path: `/${key}`,
              value: modified[key],
            };
            patchBody.push(patchValue);
          }
        }
      }

      params.body = patchBody;
      params.verb = RestMethod.PATCH;
      return `${url}/${original.id}`;
    } else {
      return url;
    }
  }

  /**
   * Http Authorization headers
   * @param params headers to replace or to add.
   * @returns HttpHeaders
   */
  private createAuthorizationHeader(params: any): { headers: HttpHeaders } {
    const lang = localStorage.getItem('lang') ? localStorage.getItem('lang') : 'es';
    const cacheIdentifier = localStorage.getItem('CacheIdentifier') ? localStorage.getItem('CacheIdentifier') : '';
    const pointSchema = localStorage.getItem('pointSchema') ? localStorage.getItem('pointSchema') : '';

    const headersConfig: { [key: string]: string } = {
      'Content-Type': 'application/json',
      accept: 'text/plain',
      'Ocp-Apim-Subscription-Key': this.envConfiguration.apimSubscriptionKey,
      language: lang,
      cache: cacheIdentifier,
      pointSchema,
      'Arkik-Request-Id': Guid.create().toString(),
      'Arkik-TimezoneOffset': `${new Date().getTimezoneOffset()}`,
      ...params?.includeSpecialHeaders,
    };

    if (params.body instanceof FormData) {
      delete headersConfig['Content-Type'];
    }

    return {
      headers: new HttpHeaders(headersConfig),
    };
  }

  /**
   * Prepare configuration by used case test or environment
   * @param point simple enpoint or test enpoint
   * @returns uriParam with enpoint && localResponse to test.
   */
  private validateForUTestResponse(point: string): {
    point: string;
    localResponse: string;
  } {
    const uriParam = {
      point: '',
      localResponse: '',
    };
    if (window.origin.includes('9876')) {
      uriParam.localResponse = 'mock';
      uriParam.point = point;
      this.envConfiguration = testCasesEnv;
    } else {
      uriParam.point = point;
      this.envConfiguration = environment;
    }

    return uriParam;
  }

  /**
   * Logs response from server only when we are working on
   * development environment.
   * @param url Url requested
   * @param data response from server
   */
  private log(url: string, data: any): void {
    this.event.emit('loadEvt', false);
    if ((this.defNotifier || data?.message) && this.event && this.event.notifierMessage) {
      const messageBody: IResponseMessage = {
        status: StatusCode.OK,
        detail: data?.message,
      };
      this.event.emit('notifierMessage', messageBody);
    }
  }

  /**
   * Send notification event to show
   * an alert message when request fails
   * @param data error message event
   */
  private error(data: any): void {
    this.event.emit('loadEvt', false);
    if (this.event && this.event.notifierMessage && data.status !== StatusCode.TOO_MANY_REQUESTS) {
      let messageBody = data?.error ? data.error : data;
      if (messageBody?.message && messageBody.message.includes('Timeout')) {
        messageBody.status = StatusCode.INTERNAL_SERVER_ERROR;
      } else {
        switch (data.status) {
          case StatusCode.SERVICE_UNAVAILABLE:
            messageBody = { details: [{ message: data.message ? data?.message : '' }] };
            messageBody.status = data.status;
            break;
          case StatusCode.ACCESS_DENIED:
            messageBody = { details: null };
            messageBody.status = data.status;
            break;
          default:
            if (typeof messageBody !== 'string') {
              messageBody.status = data.status;
            }
            break;
        }
      }
      this.event.emit('notifierMessage', messageBody);
    }
  }
}
